// Undo/redo history for the Designer canvas, extracted from Designer.tsx. Owns
// the history stack ref; the canvas ref, guides and helpers are passed in via
// deps. Verbatim logic move — snapshotHistory pushes guide-stripped snapshots
// (capped at 50), undo/redo reload them and re-add the print-area guides.
import { useRef } from 'react'
import type { RefObject, MutableRefObject } from 'react'
import { fabric } from 'fabric'

type GuideJSON = { objects?: Array<{ type?: string; strokeDashArray?: unknown; text?: string }> }

export interface UseCanvasHistoryDeps {
  fabricRef: RefObject<fabric.Canvas | null>
  switchingLocationRef: MutableRefObject<boolean>
  purgeStrayGuides: () => void
  printGuideRef: MutableRefObject<fabric.Rect | null>
  printLabelRef: MutableRefObject<fabric.Text | null>
  stripGuidesFromJSON: (json: GuideJSON) => GuideJSON
}

export function useCanvasHistory(deps: UseCanvasHistoryDeps) {
  const { fabricRef, switchingLocationRef, purgeStrayGuides, printGuideRef, printLabelRef, stripGuidesFromJSON } = deps
  const historyRef = useRef<{ stack: string[]; index: number }>({ stack: [], index: -1 })

  function undo() {
    const c = fabricRef.current; if (!c) return
    const h = historyRef.current
    if (h.index <= 0) return
    h.index--
    const json = h.stack[h.index]; if (!json) return
    switchingLocationRef.current = true
    c.loadFromJSON(json, () => {
      purgeStrayGuides()
      if (printGuideRef.current && !c.contains(printGuideRef.current)) c.add(printGuideRef.current)
      if (printLabelRef.current && !c.contains(printLabelRef.current)) c.add(printLabelRef.current)
      c.renderAll()
      window.setTimeout(() => { switchingLocationRef.current = false }, 50)
    })
  }

  function redo() {
    const c = fabricRef.current; if (!c) return
    const h = historyRef.current
    if (h.index >= h.stack.length - 1) return
    h.index++
    const json = h.stack[h.index]; if (!json) return
    switchingLocationRef.current = true
    c.loadFromJSON(json, () => {
      purgeStrayGuides()
      if (printGuideRef.current && !c.contains(printGuideRef.current)) c.add(printGuideRef.current)
      if (printLabelRef.current && !c.contains(printLabelRef.current)) c.add(printLabelRef.current)
      c.renderAll()
      window.setTimeout(() => { switchingLocationRef.current = false }, 50)
    })
  }

  // Push a history snapshot when the canvas is modified (and not via switch/undo).
  function snapshotHistory() {
    if (switchingLocationRef.current) return
    const c = fabricRef.current; if (!c) return
    // Strip guides so undo/redo can't restore selectable copies of them.
    const json = JSON.stringify(stripGuidesFromJSON(c.toJSON() as GuideJSON))
    const h = historyRef.current
    // Trim any "future" branch when we made a new edit after an undo.
    h.stack = h.stack.slice(0, h.index + 1)
    h.stack.push(json)
    if (h.stack.length > 50) h.stack.shift()
    h.index = h.stack.length - 1
  }

  return { undo, redo, snapshotHistory }
}
